<template>
  <div class="lesson-content">
    <h1>Install Git</h1>
    <p>Git is a powerful version control system used for tracking changes in code, collaborating with others, and managing project versions efficiently. It enables developers to work simultaneously without conflicts, providing a history of changes and the ability to revert or merge code effortlessly.</p>

    <h2>Step 1: Install Git</h2>
    <p>Since you already have Homebrew installed on your Mac, the easiest way to install Git is using the following command:</p>
    <div class="code-block">
      <pre><code>brew install git</code></pre>
      <button class="copy-button" @click="copyInstallCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <h2>Step 2: Verify Installation</h2>
    <p>After installation, verify Git by checking its version:</p>
    <div class="code-block">
      <pre><code>git --version</code></pre>
      <button class="copy-button" @click="copyVersionCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>You should see output similar to:</p>
    <pre><code class="output">git version 2.xx.x</code></pre>

    <h2>Initial Git Configuration</h2>
    <p>After installing Git, it's important to set up your identity. This information will be used in your commit messages:</p>
    <div class="code-block">
      <pre><code>git config --global user.name "Your Name"</code></pre>
      <button class="copy-button" @click="copyConfigNameCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <div class="code-block">
      <pre><code>git config --global user.email "your.email@example.com"</code></pre>
      <button class="copy-button" @click="copyConfigEmailCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <p class="note">Remember to replace "Your Name" and "your.email@example.com" with your actual name and email address. These will be associated with your Git commits.</p>
  </div>
</template>

<script>
export default {
  name: 'Chapter1Lesson4',
  methods: {
    async copyToClipboard(text, button) {
      await navigator.clipboard.writeText(text);
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      setTimeout(() => {
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      }, 2000);
    },
    copyInstallCommand(event) {
      const command = 'brew install git';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyVersionCommand(event) {
      const command = 'git --version';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyConfigNameCommand(event) {
      const command = 'git config --global user.name "Your Name"';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyConfigEmailCommand(event) {
      const command = 'git config --global user.email "your.email@example.com"';
      this.copyToClipboard(command, event.currentTarget);
    }
  }
}
</script>

<style scoped>
.lesson-content {
  color: var(--text);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

h1 {
  color: var(--primary);
  font-size: 2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

h2 {
  color: var(--text);
  font-size: 1.5rem;
  margin: 2rem 0 1rem;
  font-weight: 600;
}

p {
  margin-bottom: 1rem;
}

ul, ol {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

li {
  margin-bottom: 0.5rem;
}

pre {
  background: #f8fafc;
  border-radius: 6px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  border: 1px solid var(--border);
}

code {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
}

p code {
  background: #f8fafc;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.note {
  background-color: #f8fafc;
  border-left: 4px solid var(--primary);
  padding: 1rem;
  margin: 1rem 0;
  font-style: italic;
}

code.output {
  color: #16a34a;  /* Green color for output text */
}

.code-block {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.code-block pre {
  flex-grow: 1;
  margin-right: 0.5rem;
}

.copy-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--text);
  opacity: 0.7;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-button:hover {
  opacity: 1;
}

.copy-button svg {
  width: 20px;
  height: 20px;
}
</style> 